import { Component, Output, Input, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { DxTreeViewComponent, DxTreeViewTypes } from 'devextreme-angular/ui/tree-view';
import { navigation, NavigationItem } from '../../app-navigation';

import { TranslateService } from '@ngx-translate/core';
import { filter, forkJoin, Subject, takeUntil } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { RoleEnum } from 'acl-cloud-lib';
import { AuthHelperService } from '../../core/auth/auth-helper.service';


@Component({
  selector: 'app-side-navigation-menu',
  templateUrl: './side-navigation-menu.component.html',
  styleUrls: ['./side-navigation-menu.component.scss']
})
export class SideNavigationMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(DxTreeViewComponent, { static: true })
  menu!: DxTreeViewComponent;

  @Output()
  selectedItemChanged = new EventEmitter<DxTreeViewTypes.ItemClickEvent>();

  @Output()
  openMenu = new EventEmitter<any>();

  private _compactMode = false;
  @Input()
  get compactMode() {
    return this._compactMode;
  }
  set compactMode(val) {
    this._compactMode = val;

    if (!this.menu.instance) {
      return;
    }

  }

  private _selectedItem = undefined;
  private _unSubscriber = new Subject<void>();
  public items: Record <string, unknown>[] = [];
  private currentNode: any;
  private isMenuInitialized = false;

  constructor(
    private readonly _elementRef: ElementRef,
    private readonly _translateService: TranslateService,
    private readonly _router: Router,
    private readonly _authHelperService: AuthHelperService
  ) { }

  ngOnInit() {
    // Update active menu item on route change
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this._unSubscriber)
    ).subscribe((event: any) => {
      this._updateActiveMenuItem(event.urlAfterRedirects);
    });

    // generate menu
    this._authHelperService.userRights$.pipe(takeUntil(this._unSubscriber)).subscribe((roles) => {
      // deep clone navigation
      const newNavigation = navigation.map(item => ({
        ...item,
        items: item.items ? [...item.items] : []
      }));

      let menuItems = this._filterMenuItemsByRoles(newNavigation, roles);
      const translationKeys = this._getAllTextKeys(menuItems);

      forkJoin(
        translationKeys.map(key => this._translateService.get(key))
      ).pipe(takeUntil(this._unSubscriber)).subscribe(translations => {
        const translationMap: TranslationMap = {};
        translationKeys.forEach((key, index) => {
          translationMap[key] = translations[index];
        });
        this.items = menuItems.map(item => this.translateItem(item, translationMap));
        
        // Ensure menu is properly initialized after items are set
        if (this.menu?.instance) {
          this.isMenuInitialized = true;
          this._updateActiveMenuItem(this._router.url);
        }
      });
    });
  }

  ngAfterViewInit() {
    // Only update if items are already loaded
    if (this.items.length > 0) {
      this._updateActiveMenuItem(this._router.url);
    }
  }

  onItemClick(event: DxTreeViewTypes.ItemClickEvent) {
    this.currentNode = event.node;
    this.selectedItemChanged.emit(event);
  }

  // Add this method to handle TreeView initialization
  onContentReady(e: any) {
    if (!this.isMenuInitialized && this.items.length > 0) {
      this.isMenuInitialized = true;
      this._updateActiveMenuItem(this._router.url);
    }
  }

  ngOnDestroy() {
    this._unSubscriber.next();
    this._unSubscriber.complete();
  }

  /**
   * Function to translate menu items (language change)
   * @param item -> NavigationItem (one row of the menu)
   * @param translationMap -> object with all translations
   * @returns -> translated item
   */
  private translateItem(item: NavigationItem, translationMap: { [x: string]: any; }) {
    const translatedItem = { ...item };

    if(translatedItem.path && !(/^\//.test(translatedItem.path))){
      translatedItem.path = `/${translatedItem.path}`;
    }

    if (translationMap[item.text] && translationMap[item.text] !== item.text) {
      translatedItem.text = translationMap[item.text];
    } else {
      translatedItem.text = `?${item.text}?`;
    }

    if (translatedItem.items && item.items) {
      translatedItem.items = item.items.map(subItem => this.translateItem(subItem, translationMap));
    }

    return translatedItem;
  }

  /**
   * Function to get all text keys from menu items (recursive)
   * @param items -> All menu items
   * @returns -> Array with all text keys
   */
  private _getAllTextKeys(items: NavigationItem[]): string[] {
    const keys: string[] = [];

    items.forEach(item => {
      keys.push(item.text);
      if (item.items) {
        keys.push(...this._getAllTextKeys(item.items));
      }
    });

    return keys;
  }

  /**
   * Function to filter menu items by user roles
   * Menu items can have a property 'onlyForRoles' with an array of roles. If the user has one of these roles, the menu item will be displayed, otherwise not.
   * @param items -> All menu items
   * @param roles -> User roles
   * @returns -> Menu items filtered by roles
   */
  private _filterMenuItemsByRoles(items: NavigationItem[], roles: RoleEnum[]): NavigationItem[] {
    return items.filter(item => {
      if (item.items) {
        item.items = this._filterMenuItemsByRoles(item.items, roles);
      }
      if (item.onlyForRoles) {
        if (item.onlyForRoles.some(role => roles.includes(role))) {
          return item;
        } else {
          return;
        }
      } else {
        return item;
      }
    }) ?? [];
  }

  /**
   * Function to update active menu item
   * @param route -> Current route
   */
  private _updateActiveMenuItem(route: string) {
    // Don't attempt to update if menu isn't ready or items aren't loaded
    if (!this.menu?.instance || this.items.length === 0) {
      return;
    }

    setTimeout(() => {
      try {
        const item = this._findItemByRoute(this.items, route);
        if (item) {
          this.menu.instance.selectItem(item);
          this.menu.instance.expandItem(item);
        } else {
          this.menu.instance.unselectAll();
        }
      } catch (error) {
        // Silently handle if menu instance is not ready
        console.warn('Menu update failed:', error);
      }
    }, 1);
  }

  /**
   * Function to find menu item by route (recursive)
   * @param items -> All menu items
   * @param route -> Current route
   * @returns -> Menu item with the current route or null if not found.
   */
  private _findItemByRoute(items: any[], route: string): any {
    for (const item of items) {
      if (item.path === route) {
        return item;
      }
      if (item.items) {
        const subItem = this._findItemByRoute(item.items, route);
        if (subItem) {
          return subItem;
        }
      }
    }
    return null;
  }
}

interface TranslationMap {
  [key: string]: string;
}